import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Check, Loader2 } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { updateTaskCN } from 'slices/taskCNSlice'
import { taskDueIso, taskDueInputValue } from 'utils/taskTarget'
import { taskAssignees, assigneeIdOf } from '../utils/taskAssignees'

export interface FieldTaskEditSheetProps {
	/** The task to edit. Null closes the sheet. */
	task: any | null
	onClose: () => void
	/** Called after a successful save, so the caller can refetch. */
	onSaved?: () => void
}

const PRIORITIES = ['P1 - High', 'P2 - Above', 'P3 - Medium', 'P4 - Low']

/**
 * Amend a task from the field.
 *
 * The field app could create a task and list it, but never change one, so a due
 * date typed wrong in a car park needed a laptop to fix. This is the same sheet
 * for the Tasks screen and for a project, because two of them would drift and
 * the drift would be silent: a field editable in one place and not the other.
 *
 * **Only what is worth changing on a phone.** Name, due date, priority,
 * assignee, description, and marking it done. The structural keys a task hangs
 * off (`packageid`, `projectid`, `packageType`) are not offered: they were
 * derived from the project at creation and re-filing a task under a different
 * asset is a desk job, not a car-park one.
 */
export function FieldTaskEditSheet({ task, onClose, onSaved }: FieldTaskEditSheetProps) {
	const dispatch = useDispatch()

	const userId = useSelector((state: any) => state.userLogin?.userInfo?._id)
	const userName = useSelector((state: any) => state.userLogin?.userInfo?.name)
	const projectState = useSelector((state: any) => state.projects)

	/**
	 * The task's project, resolved from the store rather than from the task.
	 *
	 * `/api/taskcns` populates `projectid`, but its `partners[]` rows arrive as
	 * bare ids with no names to show. The projects slice has them populated, so
	 * that is where the list has to come from.
	 */
	const project = useMemo(() => {
		const id = assigneeIdOf(task?.projectid)
		return id ? projectState?.entities?.[id] : undefined
	}, [projectState, task])

	const [name, setName] = useState('')
	const [due, setDue] = useState('')
	const [priority, setPriority] = useState('P3 - Medium')
	const [assignee, setAssignee] = useState('')
	const [description, setDescription] = useState('')
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Shared with the add form, so the same task cannot offer one set of names
	// in one place and a different set in the other.
	const assignees = useMemo(
		() => taskAssignees({ project, userId, userName, current: task?.assignedTo }),
		[project, userId, userName, task],
	)

	// Reload the form whenever the sheet targets a different task, so it never
	// shows the previous one's values.
	useEffect(() => {
		if (!task) return
		setName(task.taskName || '')
		setDue(taskDueInputValue(task.dateDue, task.hasScheduledTime))
		setPriority(task.taskPriority || 'P3 - Medium')
		setAssignee(assigneeIdOf(task.assignedTo) || String(userId ?? ''))
		setDescription(task.description || '')
		setError(null)
		setSaving(false)
	}, [task, userId])

	const save = useCallback(
		async (patch: Record<string, unknown>) => {
			if (!task || saving) return
			setSaving(true)
			setError(null)

			const action: any = await (dispatch as any)(updateTaskCN({ _id: task._id, ...patch }))
			setSaving(false)

			// createAsyncThunk RESOLVES on rejectWithValue, so awaiting it tells
			// you nothing. Closing here regardless is what makes a refused save
			// look like it worked, and the edit is gone.
			if (action?.meta?.requestStatus === 'rejected') {
				setError(String(action.payload ?? 'That change could not be saved.'))
				return
			}
			onSaved?.()
			onClose()
		},
		[task, saving, dispatch, onSaved, onClose],
	)

	const submit = useCallback(() => {
		if (!name.trim()) return
		void save({
			taskName: name.trim(),
			taskPriority: priority,
			assignedTo: assignee || undefined,
			description,
			// Preserve whatever kind of date this already was: re-sending a
			// date-only task through taskDueIso keeps its noon-UTC sentinel, and
			// clearing the field clears the date rather than sending today.
			dateDue: due ? taskDueIso(due) : null,
		})
	}, [name, priority, assignee, description, due, save])

	const complete = useCallback(() => {
		// dateCompleted alongside it, matching what the desktop sheet writes.
		// Without it a completed task has no record of when.
		void save({ completed: true, dateCompleted: new Date().toISOString() })
	}, [save])

	if (!task) return null

	return (
		<Dialog open onClose={onClose}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>Edit task</DialogTitle>
					<DialogDescription>
						{task.projectid?.projectName || 'This task'}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 p-5 pt-2">
					<label className="block">
						<span className="text-xs font-medium text-gray-500">Task</span>
						<input
							value={name}
							onChange={e => setName(e.target.value)}
							disabled={saving}
							aria-label="Task"
							className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
							// 16px or larger, or iOS zooms the page on focus.
							style={{ fontSize: '16px' }}
							autoCapitalize="sentences"
						/>
					</label>

					<div className="flex gap-2">
						<label className="block flex-1">
							<span className="text-xs font-medium text-gray-500">Due</span>
							<input
								type="date"
								value={due}
								onChange={e => setDue(e.target.value)}
								disabled={saving}
								aria-label="Due"
								className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
								style={{ fontSize: '16px' }}
							/>
						</label>
						<label className="block flex-1">
							<span className="text-xs font-medium text-gray-500">Priority</span>
							<select
								value={priority}
								onChange={e => setPriority(e.target.value)}
								disabled={saving}
								aria-label="Priority"
								className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
								style={{ fontSize: '16px' }}
							>
								{PRIORITIES.map(p => (
									<option key={p} value={p}>
										{p}
									</option>
								))}
							</select>
						</label>
					</div>

					<label className="block">
						<span className="text-xs font-medium text-gray-500">Assigned to</span>
						<select
							value={assignee}
							onChange={e => setAssignee(e.target.value)}
							disabled={saving}
							aria-label="Assigned to"
							className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
							style={{ fontSize: '16px' }}
						>
							{assignees.map(person => (
								<option key={person.id} value={person.id}>
									{person.name}
								</option>
							))}
						</select>
					</label>

					<label className="block">
						<span className="text-xs font-medium text-gray-500">Notes</span>
						<textarea
							value={description}
							onChange={e => setDescription(e.target.value)}
							disabled={saving}
							rows={3}
							aria-label="Notes"
							className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
							style={{ fontSize: '16px' }}
						/>
					</label>

					{error && (
						<p role="alert" className="text-sm text-destructive">
							{error}
						</p>
					)}

					<div className="flex gap-2 pt-1">
						<Button variant="ghost" isInput onClick={onClose} disabled={saving} className="flex-1">
							Cancel
						</Button>
						<Button
							variant="primary"
							isInput
							onClick={submit}
							disabled={saving || !name.trim()}
							className="flex-1"
						>
							{saving ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Saving…
								</>
							) : (
								'Save'
							)}
						</Button>
					</div>

					{/*
						Completing is separated from Save: it takes the task off the
						list, which is a different kind of act from correcting a typo,
						and burying it in a dropdown makes it an accident waiting to
						happen.
					*/}
					{!task.completed && (
						<Button
							variant="ghost"
							isInput
							onClick={complete}
							disabled={saving}
							className="w-full"
						>
							<Check className="mr-2 h-4 w-4" />
							Mark complete
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default FieldTaskEditSheet
